import { useState } from 'react'
import { useActionDispatch } from '../../hooks/useActionDispatch'

interface Props {
  character: {
    active_conditions: string[] | null
    wealth_level: string | null
    wealth_score: number | null
    funds_denarii: number
    bloodloss_points: number
    fatigue_levels: number
    option_points_remaining: number
  }
  characterId?: string
  campaignId?: string
  canEdit?: boolean
}

const CONDITION_COLORS: Record<string, string> = {
  married: 'bg-blue-100 text-blue-800',
  widowed: 'bg-gray-200 text-gray-700',
  veteran: 'bg-amber-100 text-amber-800',
  scarred: 'bg-red-100 text-red-800',
  prosperous: 'bg-green-100 text-green-800',
  ruined: 'bg-red-200 text-red-900',
  destitute: 'bg-red-200 text-red-900',
  indebted: 'bg-orange-100 text-orange-800',
  landed: 'bg-emerald-100 text-emerald-800',
  guild_member: 'bg-indigo-100 text-indigo-800',
  bereaved_child: 'bg-gray-200 text-gray-700',
  orphan: 'bg-gray-200 text-gray-700',
  fostered: 'bg-purple-100 text-purple-800',
  bastard: 'bg-purple-100 text-purple-800',
  disabled: 'bg-red-100 text-red-800',
  large_family: 'bg-blue-100 text-blue-800',
  estranged: 'bg-orange-100 text-orange-800',
}

function conditionColor(condition: string): string {
  return CONDITION_COLORS[condition] ?? 'bg-parchment-dark text-ink'
}

function formatCondition(condition: string): string {
  return condition.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function StatusPanel({ character, characterId, campaignId, canEdit }: Props) {
  const { dispatch } = useActionDispatch(characterId ?? '', campaignId ?? '')
  const conditions = character.active_conditions ?? []
  const [newCondition, setNewCondition] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Status</h3>

      <div className="grid grid-cols-2 gap-1 text-sm mb-3">
        <div>
          <span className="font-semibold">Wealth:</span>{' '}
          {character.wealth_level ? (
            <span className="capitalize">{character.wealth_level}</span>
          ) : '—'}
          {character.wealth_score != null && (
            <span className="text-ink-light text-xs ml-1">({character.wealth_score})</span>
          )}
        </div>
        <div>
          <span className="font-semibold">Funds:</span>{' '}
          {character.funds_denarii}d
        </div>
        {(character.bloodloss_points > 0 || canEdit) && (
          <div className={character.bloodloss_points > 0 ? 'text-danger' : ''}>
            <span className="font-semibold">Bloodloss:</span>{' '}
            {character.bloodloss_points}
            {canEdit && (
              <span className="ml-1 inline-flex gap-0.5">
                <button
                  onClick={async () => { setBusy(true); try { await dispatch('ADD_BLOODLOSS', { amount: 1 }) } catch { /* */ } setBusy(false) }}
                  disabled={busy}
                  className="px-1.5 py-0 text-xs hover:bg-parchment-dark rounded border border-parchment-dark disabled:opacity-50"
                >+</button>
                <button
                  onClick={async () => { setBusy(true); try { await dispatch('REDUCE_BLOODLOSS', { amount: 1 }) } catch { /* */ } setBusy(false) }}
                  disabled={busy || character.bloodloss_points <= 0}
                  className="px-1.5 py-0 text-xs hover:bg-parchment-dark rounded border border-parchment-dark disabled:opacity-50"
                >-</button>
              </span>
            )}
          </div>
        )}
        {character.option_points_remaining > 0 && (
          <div>
            <span className="font-semibold">Option Points:</span> {character.option_points_remaining}
          </div>
        )}
      </div>

      <div>
        <div className="text-xs font-semibold text-ink-light uppercase mb-1">Conditions</div>
        {conditions.length > 0 ? (
          <div className="flex flex-wrap gap-1 mb-2">
            {conditions.map(c => (
              <span key={c} className={`px-2 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1 ${conditionColor(c)}`}>
                {formatCondition(c)}
                {canEdit && (
                  <button
                    onClick={async () => {
                      setBusy(true)
                      try { await dispatch('REMOVE_CONDITION', { condition: c }) } catch { /* */ }
                      setBusy(false)
                    }}
                    disabled={busy}
                    className="text-[10px] leading-none opacity-60 hover:opacity-100"
                    title={`Remove ${c}`}
                  >&times;</button>
                )}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-ink-light text-sm mb-2">No active conditions.</p>
        )}
        {canEdit && (
          <div className="flex gap-1">
            <input
              value={newCondition}
              onChange={e => setNewCondition(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
              placeholder="Add condition..."
              className="flex-1 px-2 py-0.5 border border-parchment-dark rounded text-xs"
              onKeyDown={async e => {
                if (e.key === 'Enter' && newCondition.trim()) {
                  setBusy(true)
                  try { await dispatch('APPLY_CONDITION', { condition: newCondition.trim() }) } catch { /* */ }
                  setNewCondition('')
                  setBusy(false)
                }
              }}
            />
            <button
              onClick={async () => {
                if (!newCondition.trim()) return
                setBusy(true)
                try { await dispatch('APPLY_CONDITION', { condition: newCondition.trim() }) } catch { /* */ }
                setNewCondition('')
                setBusy(false)
              }}
              disabled={busy || !newCondition.trim()}
              className="px-2 py-0.5 text-xs border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50"
            >+</button>
          </div>
        )}
      </div>
    </div>
  )
}
